import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { InformationIcon, StorageIcon } from "@/assets/icons";
import Checkbox from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import ModalHeader from "@/components/ui/modal/modalHeader";
import { RootState } from "@/store";
import { SpatialAggregationType } from "@/types/common";
import { getAvailableDatasets } from "@/utils/dataset";

import { Button } from "equator-design-system";

import DatasetAccordion from "./datasetAccordion";

const TEST_ID_PREFIX = "spatial-analysis-result-save-dataset-popup";

export interface DatasetEntry {
  datasetId: string;
  name: string;
  datasetName: string;
  description: string;
  datasetSize: number;
  spatialType: SpatialAggregationType;
}

export interface DatasetEntryFormData {
  datasetsInfo: DatasetEntry[];
}

export type SaveAsDatasetPopupProps = {
  show: boolean;
  onClose: () => void;
  onSave: (data: DatasetEntryFormData) => void;
};

const SaveAsDatasetPopup = ({
  show,
  onClose,
  onSave
}: SaveAsDatasetPopupProps) => {
  const { datasets, visualizationOrder = [] } = useSelector(
    (state: RootState) => state.dataset
  );
  const availableDatasets = getAvailableDatasets(datasets);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const orderedDatasets = visualizationOrder.map((id) => availableDatasets[id]);

  const form = useForm<DatasetEntryFormData>({
    defaultValues: {
      datasetsInfo: orderedDatasets.map((dataset) => ({
        datasetId: dataset.data.id, // Assuming dataset has an 'id' property
        name: dataset.data.display_name, // Assuming dataset has a 'name' property
        datasetSize: dataset.data.dataset_size_kb,
        datasetName: "", // Assuming dataset has a 'name' property
        description: "", // Assuming dataset has a 'description' property
        spatialType: dataset.properties.spatialType
      }))
    }
  });

  const handleSelectAll = () => {
    if (selectedItems.length < orderedDatasets.length) {
      setSelectedItems(orderedDatasets.map((dataset) => dataset.data.id));
    } else {
      setSelectedItems([]);
    }
  };

  const isDisableSave = () => {
    if (!selectedItems.length) return true;
    const fieldArray = form
      .getValues("datasetsInfo")
      .filter((dataset) => selectedItems.includes(dataset.datasetId));
    return !fieldArray.every(
      ({ datasetName, description }) =>
        datasetName?.trim().length && description?.trim().length
    );
  };

  const onSubmit = (data: DatasetEntryFormData) => {
    const datasetsInfo = data.datasetsInfo.filter((item) =>
      selectedItems.includes(item.datasetId)
    );
    onSave({
      datasetsInfo
    });
  };

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent isHideClose className="!rounded-xl w-[432px] p-0">
        <DialogTitle className="hidden" />
        <DialogDescription className="sr-only">
          Save the dataset to your Data Explorer.
        </DialogDescription>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="overflow-hidden relative"
          >
            <ModalHeader
              title="Save Dataset"
              onClose={onClose}
              titleIcon={
                <div className="w-7 h-7 bg-purple-50 rounded-lg flex-center">
                  <StorageIcon className="fill-purple-500 min-w-4 w-4" />
                </div>
              }
            />
            <div className="flex flex-col mt-2 p-4 max-h-[calc(90vh-110px)] custom-scrollbar overflow-y-auto">
              <div className="flex flex-col gap-3">
                <span className="font-bold text-txtHeading text-sm">
                  Select Dataset
                </span>
                <div className="flex ml-2">
                  <Checkbox
                    id="checkbox-dataset"
                    checked={selectedItems.length === orderedDatasets.length}
                    className="text-xs text-txtHeading"
                    onChange={handleSelectAll}
                    label="Select all"
                    data-testid="checkbox-select-all"
                  />
                </div>
              </div>
              <DatasetAccordion
                expandedItem={expandedItem}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                setExpandedItem={setExpandedItem}
              />
              <div className="flex gap-2 px-2 mt-3 my-4">
                <InformationIcon className="min-w-4" />
                <p className="text-[10px] text-txtHeading">
                  Each selected dataset will be saved separately, not as a
                  single group. You can find them in the Data Explorer menu.
                </p>
              </div>
            </div>

            <div className="flex h-[64px] px-4 gap-2 w-full">
              <Button
                className="h-[40px] w-1/2 btn-secondary text-sm font-bold"
                onClick={onClose}
              >
                <span>Cancel</span>
              </Button>
              <Button
                className="h-[40px] w-1/2 btn-primary text-sm font-bold"
                type="submit"
                disabled={isDisableSave()}
                data-testid={`${TEST_ID_PREFIX}-save-button`}
              >
                <span>Save</span>
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default SaveAsDatasetPopup;
